import { useCallback, useEffect, useRef, useState } from "react";
import debounceFn from "lodash/debounce";

export const useDebounce = <T,>(value: T, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  const debouncedSetterRef = useRef<ReturnType<typeof debounceFn> | null>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSetter = useCallback(
    debounceFn((nextValue: T) => {
      setDebouncedValue(nextValue);
    }, delay),
    [delay],
  );

  // Store ref to debounced function for access to flush/cancel
  debouncedSetterRef.current = debouncedSetter;

  useEffect(() => {
    debouncedSetter(value);
  }, [value, debouncedSetter]);

  const flush = useCallback(() => {
    debouncedSetterRef.current?.flush();
  }, []);

  const cancel = useCallback(() => {
    debouncedSetterRef.current?.cancel();
  }, []);

  return { debouncedValue, flush, cancel };
};
